import {
  assertStudioPublicationDescriptor,
  cloneStudioPublicationDescriptor,
} from "../StudioPublicationDescriptor";

const REQUIRED_IDENTITY_FIELDS = Object.freeze([
  "id",
  "version",
  "domainId",
]);

const REQUIRED_METHODS = Object.freeze([
  "validatePublication",
  "supportsDomain",
  "supportsSchemaVersion",
  "deserializePublication",
  "migratePublication",
  "resolveRuntimeConfiguration",
]);

export class StudioPublicationAdapterContractError extends Error {
  constructor(message, { issues = [] } = {}) {
    super(message);

    this.name = "StudioPublicationAdapterContractError";
    this.issues = Object.freeze(
      Array.isArray(issues)
        ? issues.map((issue) => Object.freeze({ ...issue }))
        : []
    );
  }
}

/**
 * Validate the structural contract of a StudioPublicationAdapter.
 *
 * This validation deliberately checks only the public adapter contract.
 * Domain-specific publication validation remains owned by
 * adapter.validatePublication().
 */
export function validateStudioPublicationAdapter(adapter) {
  const issues = [];

  if (!isPlainObject(adapter)) {
    return {
      valid: false,
      issues: Object.freeze([
        createIssue(
          "$",
          "invalid_type",
          "StudioPublicationAdapter must be a plain object."
        ),
      ]),
    };
  }

  for (const fieldName of REQUIRED_IDENTITY_FIELDS) {
    if (!isNonEmptyString(adapter[fieldName])) {
      issues.push(
        createIssue(
          fieldName,
          "invalid_identity",
          `${fieldName} must be a non-empty string.`
        )
      );
    }
  }

  for (const methodName of REQUIRED_METHODS) {
    if (typeof adapter[methodName] !== "function") {
      issues.push(
        createIssue(
          methodName,
          "missing_method",
          `StudioPublicationAdapter requires ${methodName}().`
        )
      );
    }
  }

  return {
    valid: issues.length === 0,
    issues: Object.freeze(issues),
  };
}

export function assertStudioPublicationAdapter(adapter) {
  const validation =
    validateStudioPublicationAdapter(adapter);

  if (!validation.valid) {
    throw new StudioPublicationAdapterContractError(
      "StudioPublicationAdapter contract validation failed.",
      {
        issues: validation.issues,
      }
    );
  }

  return adapter;
}

export function isStudioPublicationAdapter(adapter) {
  return validateStudioPublicationAdapter(adapter).valid;
}

/**
 * Create an immutable public StudioPublicationAdapter definition.
 *
 * The adapter definition itself is immutable.
 * Domain-private state must remain inside closures owned by the
 * implementation and must never be stored in the public contract.
 */
export function createStudioPublicationAdapter(definition) {
  assertStudioPublicationAdapter(definition);

  return Object.freeze({
    ...definition,
  });
}

export function getStudioPublicationAdapterRequiredIdentityFields() {
  return REQUIRED_IDENTITY_FIELDS;
}

export function getStudioPublicationAdapterRequiredMethods() {
  return REQUIRED_METHODS;
}

/**
 * Generic descriptor validation helper for adapter implementations.
 *
 * It validates the descriptor contract and enforces the adapter domain.
 * Payload-specific validation remains the responsibility of the concrete
 * adapter implementation.
 */
export function assertPublicationForAdapter(
  adapter,
  descriptor
) {
  assertStudioPublicationAdapter(adapter);
  assertStudioPublicationDescriptor(descriptor);

  if (!adapter.supportsDomain(descriptor.domainId)) {
    throw new StudioPublicationAdapterContractError(
      "Studio publication domain is not supported by this adapter.",
      {
        issues: [
          createIssue(
            "domainId",
            "unsupported_domain",
            `Adapter ${adapter.id} does not support domainId ${descriptor.domainId}.`
          ),
        ],
      }
    );
  }

  if (
    !adapter.supportsSchemaVersion(
      descriptor.schemaVersion,
      descriptor
    )
  ) {
    throw new StudioPublicationAdapterContractError(
      "Studio publication schemaVersion is not supported by this adapter.",
      {
        issues: [
          createIssue(
            "schemaVersion",
            "unsupported_schema_version",
            `Adapter ${adapter.id} does not support schemaVersion ${descriptor.schemaVersion}.`
          ),
        ],
      }
    );
  }

  return descriptor;
}

/**
 * Defensive helper for adapters that need a publication copy.
 *
 * The original StudioPublicationDescriptor must never be mutated.
 */
export function clonePublicationForAdapter(descriptor) {
  return cloneStudioPublicationDescriptor(descriptor);
}

function createIssue(path, code, message) {
  return Object.freeze({
    path,
    code,
    message,
  });
}

function isPlainObject(value) {
  if (
    value === null ||
    typeof value !== "object" ||
    Array.isArray(value)
  ) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);

  return (
    prototype === Object.prototype ||
    prototype === null
  );
}

function isNonEmptyString(value) {
  return (
    typeof value === "string" &&
    value.trim().length > 0
  );
}
